import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
export default function HowWeEvaluated() {
  const sectionRef = useRef<HTMLElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100", "translate-y-0");
          entry.target.classList.remove("opacity-0", "translate-y-8");
        }
      });
    }, {
      threshold: 0.2
    });
    if (badgeRef.current) observer.observe(badgeRef.current);
    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);
  const criteria = [{
    number: "01",
    label: "Result Longevity (Months After Stopping)"
  }, {
    number: "02",
    label: "Cost Per Month of Active Use"
  }];
  return <section ref={sectionRef} className="bg-background text-foreground overflow-hidden">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500&display=swap');`}</style>

      <div className="relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-lg opacity-5" style={{
          background: "radial-gradient(circle, #1a1a2e 0%, transparent 70%)"
        }} />
          <div className="absolute -bottom-16 -right-16 w-72 h-72 rounded-lg opacity-5" style={{
          background: "radial-gradient(circle, #1a1a2e 0%, transparent 70%)"
        }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-8 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start">

            <div className="md:col-span-5 flex flex-col gap-6">
              <div ref={badgeRef} className="opacity-0 translate-y-8 transition-all duration-700 ease-out">
                <div className="inline-flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 max-w-12" style={{
                  background: "linear-gradient(90deg, #c9a84c, transparent)"
                }} />
                  <span className="text-xs font-medium tracking-[0.25em] uppercase" style={{
                  color: "#c9a84c"
                }}>
                    Our Methodology
                  </span>
                </div>

                <div className="relative inline-block">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/approved.webp" alt="Approved Badge" className="w-12 h-12 object-contain" />
                  <div className="absolute inset-0 blur-xl opacity-20 rounded-lg" style={{
                  background: "#c9a84c"
                }} />
                </div>
              </div>

              <div ref={headingRef} className="opacity-0 translate-y-8 transition-all duration-700 delay-200 ease-out">
                <h2 className="text-2xl md:text-3xl xl:text-4xl font-black leading-[1.05] tracking-tight">
                  How We
                  <br />
                  <span style={{
                  background: "linear-gradient(135deg, #1a1a2e 0%, #4a4a6a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                    Evaluated
                  </span>
                  <br />
                  The Top 5
                </h2>
              </div>
            </div>

            <div className="md:col-span-7 flex flex-col justify-center gap-4 md:pt-8">
              <div className="hidden md:block h-px w-full mb-6" style={{
              background: "linear-gradient(90deg, transparent, #e2e2e2, transparent)"
            }} />

              {criteria.map((item, index) => <div key={item.number} data-index={index} className="group relative border border-border rounded-lg p-3 md:p-4 cursor-default transition-all duration-300 hover:border-transparent hover:shadow-xl overflow-hidden" style={{
              background: "linear-gradient(135deg, #fafafa 0%, #f4f4f4 100%)",
              transitionDelay: `${index * 80}ms`
            }}>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" style={{
                background: "linear-gradient(135deg, #1a1a2e 0%, #2d2d4e 100%)"
              }} />

                  <div className="relative z-10 flex items-center gap-6">
                    <div className="flex-shrink-0">
                      <span className="block text-3xl font-black leading-none transition-colors duration-300 group-hover:opacity-30" style={{
                    color: "#e0e0e0"
                  }}>
                        {item.number}
                      </span>
                    </div>

                    <div className="flex-1">
                      <div className="h-px w-8 mb-3 transition-colors duration-300" style={{
                    background: "#c9a84c"
                  }} />
                      <h3 className="text-lg md:text-xl font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-white">
                        {item.label}
                      </h3>
                    </div>

                    <div className="flex-shrink-0 w-8 h-8 rounded-lg border border-border flex items-center justify-center transition-all duration-300 group-hover:border-white/30 group-hover:bg-white/10">
                      <svg className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>)}

              <div className="mt-4 flex items-center gap-3 px-4">
                <div className="h-1 w-1 rounded-lg" style={{
                background: "#c9a84c"
              }} />
                <p className="text-xs tracking-widest uppercase text-muted-foreground">
                  Rigorous independent testing criteria
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}